export const orderScore = (scores = {}) => {
  const answered = Object.values(scores).filter(
    (v) => typeof v === "number" && v > 0,
  );
  if (!answered.length) return 0;
  return answered.reduce((a, b) => a + b, 0) / answered.length;
};

const mean = (values) =>
  values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;

const emptyDistribution = () => ({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });

const toPercent = (counts, total) => {
  const out = {};
  [5, 4, 3, 2, 1].forEach((k) => {
    out[k] = total ? Math.round((counts[k] / total) * 100) : 0;
  });
  return out;
};

export const aggregateReviews = (reviews = []) => {
  const published = reviews.filter((r) => r?.published !== false);
  const counts = emptyDistribution();
  published.forEach((r) => {
    const rounded = Math.round(orderScore(r.scores));
    if (counts[rounded] !== undefined) counts[rounded] += 1;
  });

  return {
    count: published.length,
    score: mean(published.map((r) => orderScore(r.scores))),
    distribution: counts,
    percentages: toPercent(counts, published.length),
    subScores: {
      freshness: mean(
        published.map((r) => r.scores?.freshness).filter(Boolean),
      ),
      delivery: mean(published.map((r) => r.scores?.delivery).filter(Boolean)),
      support: mean(published.map((r) => r.scores?.support).filter(Boolean)),
    },
  };
};

export const aggregateItemRatings = (ratings = []) => {
  const counts = emptyDistribution();
  ratings.forEach((r) => {
    if (counts[r.stars] !== undefined) counts[r.stars] += 1;
  });

  return {
    count: ratings.length,
    score: mean(ratings.map((r) => r.stars)),
    distribution: counts,
    percentages: toPercent(counts, ratings.length),
  };
};

export const formatScore = (value) => (Math.round(value * 10) / 10).toFixed(1);

export const relativeDate = (input) => {
  if (!input) return "";
  const then = new Date(input).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

export const shortDate = (input) => {
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export const shortDateWithDay = (input) => {
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

export const displayName = (firstName, lastName) => {
  const first = (firstName || "").trim();
  const initial = (lastName || "").trim().charAt(0);
  if (!first) return "Vshopper";
  return initial ? `${first} ${initial.toUpperCase()}.` : first;
};

export const isWithinReviewWindow = (deliveredAt, windowDays = 30) => {
  if (!deliveredAt) return false;
  const delivered = new Date(deliveredAt).getTime();
  if (Number.isNaN(delivered)) return false;
  return Date.now() - delivered <= windowDays * 86400000;
};

export const canReviewOrder = (order, windowDays = 30) =>
  !!order &&
  !order.rated &&
  isWithinReviewWindow(order.deliveredAt, windowDays);
